import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  BookOpen, 
  Sun, 
  Music, 
  ArrowRight, 
  Feather, 
  Mic2,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Play,
  Pause,
  Volume2,
  VolumeX,
  SkipBack, 
  SkipForward,
  Grid
} from 'lucide-react';
import { courseModules } from '../data/courseModules';
import Modal from '../components/Modal';
import Quiz from '../components/Quiz';
import FeedbackForm from '../components/FeedbackForm';
import './EventDetailsPageTemplateTwo.css';

/* --- 2. MAIN COMPONENT --- */
const EventDetailsPageTemplateTwo = () => {
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const scrollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const module = params.get('module');
    if (module) {
      const moduleIndex = courseModules.findIndex(m => m.id === module);
      if (moduleIndex !== -1) {
        setActiveModuleIndex(moduleIndex);
      }
    }
  }, [location.search]);

  const currentModule = courseModules[activeModuleIndex];
  const currentPage = currentModule.pages ? currentModule.pages[currentPageIndex] : null;

  // Add a safeguard for when pages might not be defined or currentPageIndex is out of bounds
  if (!currentPage && !currentModule.isPlaceholder) {
    return (
      <div className="app-external-wrapper">
        <div className="app-frame">
          <h2 style={{textAlign: 'center', marginTop: '50px'}}>Page content not found for this module.</h2>
        </div>
      </div>
    );
  }

  // Modified scroll handler for internal frame
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element && scrollRef.current) {
        const containerTop = scrollRef.current.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;
        const offset = elementTop - containerTop + scrollRef.current.scrollTop;
        
        scrollRef.current.scrollTo({
            top: offset,
            behavior: 'smooth'
        });
    }
  };

  const handleModuleChange = (index) => {
    setActiveModuleIndex(index);
    setCurrentPageIndex(0); // Reset page index when module changes
    setIsMenuOpen(false);
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPageContent = () => {
    if (!currentPage) return null;

    switch (currentPage.pageId) {
      case 'definition':
        return (
          <section className="section-padding bg-gray">
            <div className="container">
              <div className="grid-2">
                <div className="img-card" style={{order: 1}}>
                  <img src={currentPage.definition.image} alt={currentPage.definition.imageAlt} />
                </div>
                <div style={{order: 2}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', color:'#D97706'}}>
                    <BookOpen size={24} /> <span className="section-label" style={{marginBottom:0}}>{currentPage.definition.sectionLabel}</span>
                  </div>
                  <h2 className="section-title" style={{marginTop:'10px'}}>{currentPage.definition.title}</h2>
                  <p style={{fontSize:'1.1rem'}}>{currentPage.definition.text}</p>
                  <div style={{borderLeft:'4px solid #D97706', paddingLeft:'20px', margin:'30px 0', fontStyle:'italic', color:'#555'}}>
                    <p><strong>{currentPage.definition.quote.origin}</strong> "{currentPage.definition.quote.sanskrit}"</p>
                    <p style={{marginTop:'10px'}}>— “{currentPage.definition.quote.meaning}”</p>
                  </div>
                  <p style={{color:'#4B5563'}}>{currentPage.definition.description}</p>
                </div>
              </div>
            </div>
          </section>
        );
      case 'origin':
        return (
          <section className="section-padding bg-white">
            <div className="container">
              <div className="grid-2">
                <div>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', color:'#D97706'}}>
                    <Feather size={24} /> <span className="section-label" style={{marginBottom:0}}>{currentPage.origin.sectionLabel}</span>
                  </div>
                  <h2 className="section-title" style={{marginTop:'10px'}}>{currentPage.origin.title}</h2>
                  {currentPage.origin.cards.map((card, idx) => (
                    <div key={idx} style={{background:'#FEF3C7', padding:'24px', borderRadius:'12px', border:'1px solid #FDE68A', marginBottom:'20px'}}>
                      <h3 style={{fontSize:'1.2rem', marginBottom:'10px', color:'#78350F'}}>{card.title}</h3>
                      <p>{card.text}</p>
                    </div>
                  ))}
                </div>
                <div className="img-card"><img src={currentPage.origin.image} alt={currentPage.origin.imageAlt} /></div>
              </div>
            </div>
          </section>
        );
      case 'shruti':
        return (
          <>
            <section className="section-padding bg-white">
              <div className="container">
                <div className="grid-2">
                   <div className="img-card"><img src={currentPage.shruti.image} alt={currentPage.shruti.imageAlt} /></div>
                   <div>
                      <span className="section-label">{currentPage.shruti.sectionLabel}</span>
                      <h2 className="section-title">{currentPage.shruti.title}</h2>
                      <p style={{fontSize:'1.1rem'}}>{currentPage.shruti.text}</p>
                      <p style={{color: '#D97706', fontWeight: 600, marginTop: '20px'}}>{currentPage.shruti.highlight}</p>
                   </div>
                </div>
              </div>
            </section>
            <section className="section-padding bg-gray text-center">
              <div className="container" style={{maxWidth: '800px'}}>
                <Sun size={32} color="#D97706" style={{marginBottom:'20px'}} />
                <h2 className="section-title">{currentPage.limitless.title}</h2>
                <p style={{fontSize:'1.4rem', fontStyle:'italic', color:'#555', marginBottom:'40px'}}>"{currentPage.limitless.quote}"</p>
                <img src={currentPage.limitless.image} style={{width:'100%', borderRadius:'12px'}} alt={currentPage.limitless.imageAlt} />
              </div>
            </section>
          </>
        );
      case 'structure':
        return (
          <section className="section-padding bg-white">
            <div className="container">
              <div className="text-center" style={{maxWidth:'700px', margin:'0 auto 50px'}}>
                <h2 className="section-title">{currentPage.structure.title}</h2>
                <p style={{fontSize:'1.1rem', color:'#555'}}>{currentPage.structure.description}</p>
              </div>
              <div className="grid-3">
                {currentPage.structure.cards.map((card, idx) => (
                  <div key={idx} className="structure-card">
                    {/* {getStructureIcon(card.id)} */}
                    <h3 style={{fontSize:'1.2rem', margin:'10px 0'}}>{card.title}</h3>
                    <p style={{color:'#4B5563', fontSize:'0.9rem'}}>{card.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case 'purpose':
        return (
          <section className="section-padding bg-gray">
            <div className="container">
              <div className="grid-2">
                 <div>
                    <h2 className="section-title">{currentPage.purpose.essence.title}</h2>
                    <p style={{fontSize:'1.1rem'}}>{currentPage.purpose.essence.text}</p>
                    <p style={{marginTop:'10px', color:'#555'}}>{currentPage.purpose.essence.subText}</p>
                 </div>
                 <div>
                    <h2 className="section-title">{currentPage.purpose.universality.title}</h2>
                    <p style={{fontSize:'1.1rem'}}>{currentPage.purpose.universality.text}</p>
                    <p style={{marginTop:'10px', color:'#555'}}>{currentPage.purpose.universality.subText}</p>
                 </div>
              </div>
              <div style={{marginTop:'40px'}}>
                 <img src={currentPage.purpose.image} style={{width:'100%', borderRadius:'12px', height:'300px', objectFit:'cover'}} alt="Nature" />
              </div>
            </div>
          </section>
        );
      case 'sound':
        return (
          <section className="section-padding dark-section">
            <div style={{position:'absolute', inset:0, zIndex:0}}><img src={currentPage.sound.bgImage} alt={currentPage.sound.bgAlt} style={{width:'100%', height:'100%', objectFit:'cover', opacity:0.1}} /></div>
            <div className="container text-center" style={{position: 'relative', zIndex: 10}}>
              <Music size={32} color="#FBBF24" style={{marginBottom:'20px'}} />
              <h2 className="section-title" style={{color:'white'}}>{currentPage.sound.title}</h2>
              <p style={{fontSize:'1.3rem', color:'#D1D5DB', maxWidth:'800px', margin:'0 auto 30px'}}>{currentPage.sound.description}</p>
              <div className="grid-2 text-center" style={{textAlign:'left'}}>
                <div style={{background:'rgba(255,255,255,0.05)', padding:'30px', borderRadius:'16px'}}>
                  <h3 style={{color:'#FBBF24', fontSize:'1.2rem'}}>{currentPage.sound.mantraCard.title}</h3>
                  <ul style={{listStyle:'none', padding:0}}>
                    {currentPage.sound.mantraCard.points.map((pt, i) => (
                      <li key={i} style={{display:'flex', gap:'10px', marginBottom:'10px'}}>
                        <ArrowRight size={20} /> <strong>{pt.label}:</strong> {pt.value}
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{background:'rgba(255,255,255,0.05)', padding:'30px', borderRadius:'16px'}}>
                  <h3 style={{color:'#FBBF24', fontSize:'1.2rem'}}>{currentPage.sound.svaraCard.title}</h3>
                  <ul style={{listStyle:'none', padding:0}}>
                    {currentPage.sound.svaraCard.list.map((it, i) => <li key={i} style={{marginBottom:'5px'}}>• {it}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      case 'preservation':
        return (
          <section className="section-padding bg-white">
            <div className="container">
              <div className="grid-2">
                <div className="img-card"><img src={currentPage.preservation.image} alt={currentPage.preservation.imageAlt} /></div>
                <div>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', color:'#D97706'}}>
                    <Mic2 size={24} /> <span className="section-label">{currentPage.preservation.sectionLabel}</span>
                  </div>
                  <h2 className="section-title" style={{marginTop:'10px'}}>{currentPage.preservation.title}</h2>
                  <p style={{fontSize:'1.1rem'}}>{currentPage.preservation.text}</p>
                  <div style={{display:'flex', flexWrap:'wrap', gap:'10px', marginTop:'20px'}}>
                    {currentPage.preservation.techniques.map((tech, i) => (
                      <span key={i} style={{background:'#FEF3C7', color:'#92400E', padding:'8px 16px', borderRadius:'6px', fontWeight:600}}>{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      case 'relevance':
        return (
          <section className="section-padding bg-gray">
            <div className="container" style={{maxWidth:'800px'}}>
              <div className="text-center" style={{marginBottom:'40px'}}>
                <h2 className="section-title">{currentPage.relevance.title}</h2>
              </div>
              {currentPage.relevance.content.map((item, idx) => (
                <KeyTakeaway key={idx} question={item.q} answer={item.a} />
              ))}
            </div>
          </section>
        );
      case 'quiz':
        return (
          <section className="section-padding bg-white text-center">
            <div className="container" style={{maxWidth:'600px'}}>
              <h2 className="section-title">Module Quiz</h2>
              <p>Click the "Take a Quiz" button below to start the quiz for this module.</p>
            </div>
          </section>
        );
      case 'feedback':
        return (
          <section className="section-padding bg-gray text-center">
            <div className="container" style={{maxWidth:'600px'}}>
              <h2 className="section-title">Module Feedback</h2>
              <p>Click the "Give Feedback" button below to provide feedback for this module.</p>
            </div>
          </section>
        );
      default:
        return (
          <section className="section-padding bg-gray">
            <div className="container text-center">
              <h2 className="section-title">Page Content for {currentPage.title}</h2>
              <p>This is a placeholder for content with pageId: {currentPage.pageId}.</p>
            </div>
          </section>
        );
    }
  };

console.log('data111-courseModules-templateTwo',courseModules)


  return (
    <div className="app-external-wrapper">
      {/* --- APP FRAME --- */}
      <div className="app-frame">
        
        {/* 1. UPDATED FRAME HEADER (LIGHT THEME) */}
        <header className="frame-navbar">
          <div className="nav-logo-area">
            <Sun color="#D97706" size={24} /> 
            <span style={{color: '#1F2937'}}>SAI SRUTI</span>
          </div>
          
          {/* Central Module Details */}
          <div className="nav-center-info">
            <h2 className="nav-module-title" style={{color: '#1F2937'}}>
              {currentModule.title === "Prologue & Epilogue" ? "Introduction" : currentModule.title}
            </h2>
            <div className="nav-module-subtitle">{currentModule.subtitle}</div>
            {/* <div className="nav-module-tagline">Eternal Truths • Universal Knowledge</div> */}
          </div>
          
          <div className="nav-controls">
            <button className="module-selector-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <Grid size={16} color="#1F2937"/> Jump to...
              <ChevronDown size={14} color="#1F2937" style={{transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s'}} />
            </button>

            <div className={`module-dropdown ${isMenuOpen ? 'open' : ''}`}>
              {courseModules.map((mod, idx) => (
                <div 
                  key={mod.id} 
                  className={`dropdown-item ${activeModuleIndex === idx ? 'active' : ''}`}
                  onClick={() => handleModuleChange(idx)}
                >
                  <div style={{fontSize: '0.8rem', color: '#6B7280', textTransform: 'uppercase'}}>{idx === 0 ? 'Start Here' : idx === 13 ? 'Bonus' : `Module ${idx}`}</div>
                  <div style={{fontWeight: 600}}>{mod.subtitle ? mod.subtitle.split(":")[0] : mod.title}</div>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* 2. FRAME CONTENT */}
        <div className="frame-content" ref={scrollRef}>
          
          {/* HERO */}
          <section className="hero">
            <div className="hero-bg">
              <img src={currentModule.hero.bgImage} alt={currentModule.hero.bgAlt} />
            </div>
            <div className="hero-overlay"></div>
            <div className="hero-content">
              <div style={{color: '#FCD34D', fontSize: '1.1rem', marginBottom: '0.5rem'}}>{currentModule.hero.subtitle}</div>
              <h1 className="hero-title">{currentModule.hero.title}</h1>
              <div style={{width: '80px', height: '3px', background: '#D97706', margin: '0 auto 1.5rem'}}></div>
              <p style={{fontSize: '1.2rem', fontWeight: 300, marginBottom: '2rem'}}>
                {currentModule.hero.description}
              </p>
              {!currentModule.isPlaceholder && (
                <button onClick={() => scrollToSection('content-start')} className="hero-btn">
                  {currentModule.hero.buttonText} <ChevronDown size={18} />
                </button>
              )}
              <button onClick={() => scrollToSection('audio-player')} className="hero-btn">
                Listen to Audio <Music size={18} />
              </button>
            </div>
          </section>

          {/* CONTENT RENDERER */}
          {currentModule.isPlaceholder ? (
            <div className="placeholder-container bg-gray">
              <Sun size={50} color="#D97706" style={{marginBottom: '20px', opacity: 0.5}} />
              <h2 className="section-title" style={{fontSize: '1.8rem'}}>Coming Soon</h2>
              <p style={{fontSize: '1.1rem', color: '#666', maxWidth: '500px'}}>
                We are currently curating the divine content for <strong>{currentModule.title}: {currentModule.subtitle}</strong>. 
                Please check back later.
              </p>
            </div>
          ) : (
            renderPageContent()
          )}

          {/* LINEAR NAVIGATION FOOTER */}
          <div className="container">
            <div className="module-footer-nav">
              <button className="nav-btn-large" onClick={() => setIsQuizOpen(true)}>
                Take a Quiz
              </button>
              <button className="nav-btn-large" onClick={() => setIsFeedbackOpen(true)}>
                Give Feedback
              </button>
              {currentPageIndex > 0 ? (
                <div className="nav-btn-large" onClick={() => setCurrentPageIndex(currentPageIndex - 1)}>
                  <div>
                    <span className="nav-btn-label">Previous Page</span>
                    <div className="nav-btn-title" style={{fontSize:'1rem'}}>{currentModule.pages[currentPageIndex - 1].title}</div>
                  </div>
                  <ChevronLeft size={20} color="#9CA3AF" />
                </div>
              ) : <div style={{flex:1}}></div>}

              {currentPageIndex < currentModule.pages.length - 1 ? (
                <div className="nav-btn-large" onClick={() => setCurrentPageIndex(currentPageIndex + 1)} style={{justifyContent: 'flex-end', textAlign:'right'}}>
                  <div>
                    <span className="nav-btn-label">Next Page</span>
                    <div className="nav-btn-title" style={{fontSize:'1rem'}}>{currentModule.pages[currentPageIndex + 1].title}</div>
                  </div>
                  <ChevronRight size={20} color="#D97706" style={{marginLeft:'15px'}} />
                </div>
              ) : <div style={{flex:1}}></div>}
            </div>
          </div>
        </div>

        {/* 3. FRAME AUDIO PLAYER (LIGHT THEME) */}
        <div className="frame-audio-player" id="audio-player">
          <AudioPlayer audioData={currentModule.audio || {title: "No Audio", audioUrl: "", duration: "0:00"}} />
        </div>

      </div>
      {isQuizOpen && (
        <Modal onClose={() => setIsQuizOpen(false)} title="Module Quiz">
          <Quiz questions={currentModule.quiz?.questions || []} />
        </Modal>
      )}
      {isFeedbackOpen && (
        <Modal onClose={() => setIsFeedbackOpen(false)} title="Give Feedback">
          <FeedbackForm moduleId={currentModule.id} />
        </Modal>
      )}
    </div>
  );
};

/* --- HELPER COMPONENTS --- */

const AudioPlayer = ({ audioData }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if(audioRef.current) {
      audioRef.current.load();
      setIsPlaying(false);
      setProgress(0);
    }
  }, [audioData.audioUrl]);

  const togglePlay = () => {
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    const current = audioRef.current.currentTime;
    const duration = audioRef.current.duration || 1;
    setProgress((current / duration) * 100);
  };

  const skip = (val) => { audioRef.current.currentTime += val; };

  return (
    <div>
      <audio ref={audioRef} src={audioData.audioUrl} onTimeUpdate={handleTimeUpdate} onEnded={() => setIsPlaying(false)} />
      <div className="audio-controls">
        <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
          <button className="skip-btn" onClick={() => skip(-10)}><SkipBack size={18} /></button>
          <button className="play-btn" onClick={togglePlay}>
            {isPlaying ? <Pause size={20} color="white" /> : <Play size={20} color="white" style={{marginLeft:'2px'}} />}
          </button>
          <button className="skip-btn" onClick={() => skip(10)}><SkipForward size={18} /></button>
        </div>
        <div style={{flexGrow:1}}>
          <div style={{fontWeight:600, fontSize:'0.85rem', color:'#1F2937', marginBottom:'5px'}}>{audioData.title}</div>
          <div style={{height:'4px', background:'#E5E7EB', borderRadius:'2px', cursor:'pointer'}}>
            <div style={{height:'100%', width:`${progress}%`, background:'#D97706', borderRadius:'2px'}}></div>
          </div>
        </div>
        <div className="volume-controls" style={{display:'flex', alignItems:'center', gap:'8px'}}>
          <button className="volume-btn" onClick={() => { audioRef.current.muted = !isMuted; setIsMuted(!isMuted); }}>
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} 
                 onChange={(e) => { setVolume(e.target.value); audioRef.current.volume = e.target.value; setIsMuted(e.target.value === "0"); }} 
                 style={{width:'60px', height:'4px', accentColor:'#D97706'}} />
        </div>
      </div>
    </div>
  );
};

const KeyTakeaway = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={{border:'1px solid #FDE68A', borderRadius:'8px', overflow:'hidden', marginBottom:'15px', background:'white'}}>
      <button onClick={() => setIsOpen(!isOpen)} style={{width:'100%', textAlign:'left', padding:'20px', background:'#FEF3C7', border:'none', cursor:'pointer', display:'flex', justifyContent:'space-between', fontWeight:700, color:'#78350F'}}>
        {question} <ChevronDown size={20} style={{transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition:'0.3s'}} />
      </button>
      <div style={{padding: isOpen ? '20px' : '0 20px', maxHeight: isOpen ? '500px' : '0', overflow:'hidden', transition:'all 0.3s'}}>
        <p style={{color:'#374151'}}>{answer}</p>
      </div>
    </div>
  );
};

export default EventDetailsPageTemplateTwo;